import React, { useMemo } from "react";
import { Divider } from "@material-ui/core";
import { getConfigTenant, isCorpTenant, isVesperTenant } from "@capone/common";
import {
  buildPayFlexModalCopy,
  CurrencyFormatters,
  PayFlexPaymentOptionsTile,
  PayFlexSurveyModal,
  useDeviceTypes,
  usePayFlexSurvey,
} from "halifax";
import {
  COT_PAYMENT_FLEX_SURVEY,
  getExperimentVariantCustomVariants,
  PAY_FLEX_SURVEY_VARIANTS,
  useExperiments,
} from "../../../../context/experiments";
import { trackEvent } from "../../../../api/v0/analytics/trackEvent";
import { fetchUserMetadata } from "../../../../api/v0/user/fetchUserMetadata";
import { updateUserMetadata } from "../../../../api/v0/user/updateUserMetadata";

export interface IPayFlexSurveyProps {
  netCashValueP: number;
  totalAmountUsd: number;
  rewardsAmountUsd: number;
  creditsAmountUsd: number;
  cardProductUsed: string;
}

export const PayFlexSurvey: React.FC<IPayFlexSurveyProps> = ({
  netCashValueP,
  totalAmountUsd,
  rewardsAmountUsd,
  creditsAmountUsd,
  cardProductUsed,
}: IPayFlexSurveyProps) => {
  const { experiments } = useExperiments();
  const { matchesMobile } = useDeviceTypes();

  // Scoped to the consumer (capone) tenant.
  const tenant = getConfigTenant();
  const enabled = !isCorpTenant(tenant) && !isVesperTenant(tenant);

  const baseTrackingProperties = useMemo(
    () => ({
      card_product_used: cardProductUsed,
      travel_product: "hotel" as const,
      total_amount_usd: totalAmountUsd,
      card_amount_usd: netCashValueP,
      rewards_amount_usd: rewardsAmountUsd,
      credits_amount_usd: creditsAmountUsd,
    }),
    [cardProductUsed, totalAmountUsd, netCashValueP, rewardsAmountUsd, creditsAmountUsd]
  );

  const variant = getExperimentVariantCustomVariants(
    experiments,
    COT_PAYMENT_FLEX_SURVEY,
    PAY_FLEX_SURVEY_VARIANTS
  );

  const {
    showTile,
    showLink,
    isModalOpen,
    modalPhase,
    selectedOption,
    openSurvey,
    selectOption,
    onSubmit,
    onDismiss,
    onReturnToCheckout,
  } = usePayFlexSurvey({
    enabled,
    isDataReady: netCashValueP > 0,
    variant,
    baseTrackingProperties,
    trackEvent,
    fetchUserMetadata,
    updateUserMetadata,
  });

  if (!showTile) {
    return null;
  }

  const copy = buildPayFlexModalCopy(netCashValueP, variant, (value) =>
    CurrencyFormatters.get().format(value)
  );

  return (
    <>
      <Divider className="payment-methods-container-divider" />
      <PayFlexPaymentOptionsTile
        amountDue={CurrencyFormatters.get().format(netCashValueP)}
        showLink={showLink}
        onLinkClick={openSurvey}
        isMobile={matchesMobile}
      />
      <PayFlexSurveyModal
        open={isModalOpen}
        phase={modalPhase}
        copy={copy}
        selectedOption={selectedOption}
        onSelectOption={selectOption}
        onSubmit={onSubmit}
        onDismiss={onDismiss}
        onReturnToCheckout={onReturnToCheckout}
        isMobile={matchesMobile}
      />
    </>
  );
};
